// 대화 ⋯ 메뉴 — 이름 변경 · 고정 · 그룹으로 이동(2차 메뉴) · 링크 복사 · 삭제.
// 사이드바 행과 상단 제목(⋯) 두 곳에서 같은 메뉴를 쓴다 (바닐라 openConvMenu).
import { useState } from "react";
import { groupNames, useChat } from "../../stores/chat";
import ConvMenu, { type MenuItem } from "./ConvMenu";

interface Props {
  id: string;
  anchor: HTMLElement;
  onClose: () => void;
  /** 이름 변경 시작 — 호출측이 인라인 입력을 띄운다 (사이드바 행 / 상단 제목) */
  onRenameStart: (id: string) => void;
  onDelete: (id: string) => void;
}

const stroke = {
  fill: "none",
  stroke: "currentColor",
  strokeWidth: 1.7,
  strokeLinecap: "round" as const,
  strokeLinejoin: "round" as const,
};
const ICON_RENAME = (
  <svg viewBox="0 0 24 24" {...stroke}>
    <path d="M12 20h9" />
    <path d="M16.5 3.5a2.1 2.1 0 0 1 3 3L7 19l-4 1 1-4z" />
  </svg>
);
const ICON_PIN = (
  <svg viewBox="0 0 24 24" {...stroke}>
    <path d="M12 17v5" />
    <path d="M9 2h6l-1 6 3 3v2H7v-2l3-3z" />
  </svg>
);
const ICON_FOLDER = (
  <svg viewBox="0 0 24 24" {...stroke}>
    <path d="M22 19a2 2 0 0 1-2 2H4a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h5l2 3h9a2 2 0 0 1 2 2z" />
  </svg>
);
const ICON_LINK = (
  <svg viewBox="0 0 24 24" {...stroke}>
    <path d="M10 13a5 5 0 0 0 7.5.5l3-3a5 5 0 0 0-7-7l-1.5 1.5" />
    <path d="M14 11a5 5 0 0 0-7.5-.5l-3 3a5 5 0 0 0 7 7l1.5-1.5" />
  </svg>
);
const ICON_TRASH = (
  <svg viewBox="0 0 24 24" {...stroke}>
    <path d="M3 6h18" />
    <path d="M8 6V4h8v2" />
    <path d="M19 6l-1 14H6L5 6" />
  </svg>
);

export default function SessionMenu({ id, anchor, onClose, onRenameStart, onDelete }: Props) {
  const sessions = useChat((st) => st.sessions);
  const userKey = useChat((st) => st.userKey);
  const [mode, setMode] = useState<"main" | "move">("main");
  const [copied, setCopied] = useState(false);
  const s = sessions.find((x) => x.id === id);
  if (!s) return null;

  if (mode === "move") {
    const names = groupNames(userKey, sessions);
    const items: MenuItem[] = [
      ...names.map((g): MenuItem => ({
        key: `g:${g}`,
        label: `${g}${s.group === g ? " ✓" : ""}`,
        icon: ICON_FOLDER,
      })),
      ...(names.length ? (["sep"] as MenuItem[]) : []),
      { key: "__new__", label: "+ 새 그룹 만들기", icon: ICON_FOLDER },
      ...(s.group ? ([{ key: "__none__", label: "그룹에서 빼기" }] as MenuItem[]) : []),
    ];
    return (
      <ConvMenu
        anchor={anchor}
        items={items}
        onPick={(k) => {
          onClose();
          const store = useChat.getState();
          let g: string | undefined;
          if (k === "__new__") {
            g = (window.prompt("새 그룹 이름") || "").trim().slice(0, 30);
            if (!g) return;
            store.addGroup(g);
          } else if (k === "__none__") g = undefined;
          else g = k.slice(2);
          store.setSessionGroup(id, g);
        }}
        onClose={onClose}
      />
    );
  }

  const items: MenuItem[] = [
    { key: "rename", label: "이름 변경", icon: ICON_RENAME },
    { key: "pin", label: s.pinned ? "고정 해제" : "고정", icon: ICON_PIN },
    { key: "group", label: `그룹으로 이동${s.group ? ` (${s.group})` : ""}`, icon: ICON_FOLDER },
    { key: "link", label: copied ? "✓ 링크 복사됨" : "링크 복사", icon: ICON_LINK },
    "sep",
    { key: "delete", label: "삭제", icon: ICON_TRASH, danger: true },
  ];
  return (
    <ConvMenu
      anchor={anchor}
      items={items}
      onPick={(k) => {
        if (k === "link") {
          // 복사 피드백을 보여주고 닫는다 (닫기 전에 처리)
          const url = location.origin + "/c/" + encodeURIComponent(id);
          const done = () => {
            setCopied(true);
            setTimeout(onClose, 700);
          };
          if (navigator.clipboard && navigator.clipboard.writeText) {
            navigator.clipboard.writeText(url).then(done, () => {
              window.prompt("이 대화의 링크:", url);
              onClose();
            });
          } else {
            window.prompt("이 대화의 링크:", url);
            onClose();
          }
          return;
        }
        if (k === "group") {
          setMode("move"); // 같은 앵커에서 2차 메뉴로 전환
          return;
        }
        onClose();
        if (k === "rename") onRenameStart(id);
        else if (k === "pin") useChat.getState().togglePin(id);
        else if (k === "delete") onDelete(id);
      }}
      onClose={onClose}
    />
  );
}
